// The records that /search and the command palette look through. One index, built from the live collections, so both
// return the same records. Each entry says where it opens. Search text is case- and accent-insensitive.
import { money } from "@offsilo/ui/format";
import type { CollectionName } from "@/data/client";
import { fxDisplay, type Amount, type FxRates } from "./fx";

export const RECORD_KINDS = [
  "client", "project", "task", "invoice", "person", "contact", "deal", "doc",
  "proposal", "file", "ticket", "conversation", "agreement", "message", "view",
] as const;
export type RecordKind = (typeof RECORD_KINDS)[number];

/** The collection each kind of record is stored in (its field history and restores go to it). */
export const COLLECTION_OF: Record<RecordKind, CollectionName> = {
  client: "clients",
  project: "projects",
  task: "tasks",
  invoice: "invoices",
  person: "people",
  contact: "contacts",
  deal: "deals",
  doc: "docs",
  proposal: "proposals",
  file: "files",
  ticket: "tickets",
  conversation: "conversations",
  agreement: "agreements",
  message: "messages",
  view: "savedViews",
};

/** What a favourite, a recent item or a preview needs to show and open a record. */
export type RecordRef = { kind: RecordKind; id: string; title: string; href: string; meta?: string };
/** A record in the index: its reference plus the folded text that search matches. */
export type IndexedRecord = RecordRef & { text: string };

/** The rows the index reads. The contract rows carry more fields; these are the ones an entry needs. */
export type IndexSource = {
  clients: { id: string; slug: string; name: string; code: string }[];
  projects: { id: string; slug: string; name: string; key: string }[];
  tasks: { id: string; key: string; title: string; projectId: string }[];
  invoices: { id: string; number: string; total: { amount: number; currency: string } }[];
  people: { id: string; name: string; email?: string | null }[];
  contacts: { id: string; name: string; email?: string | null }[];
  deals: { id: string; title: string; value: { amount: number; currency: string } }[];
  docs: { id: string; title: string }[];
  /** The six kinds the server search answers (X11-614a). Optional: a caller that does not load them gets none of them. */
  proposals?: { id: string; title: string; value: { amount: number; currency: string } }[];
  files?: { id: string; name: string; folderId: string }[];
  tickets?: { id: string; number: string; subject: string }[];
  conversations?: { id: string; subject: string }[];
  agreements?: { id: string; title: string }[];
  messages?: { id: string; conversationId: string; fromName: string; body: string }[];
};

/** One row of an index source collection. */
export type IndexRow<K extends keyof IndexSource> = NonNullable<IndexSource[K]>[number];

/** Lowercase, accents removed: "Café" and "cafe" match the same search. */
export const foldText = (s: string) => s.normalize("NFKD").replace(/\p{Diacritic}/gu, "").toLowerCase();

/** How amounts read in the index: `currency` is the display currency (absent or null shows each amount as it is) and
 *  `rates` converts to it (units of each currency per one unit of a shared base). A missing rate shows the amount alone. */
export type IndexDisplay = { currency?: string | null; rates?: FxRates };

/** An amount as the index shows it: its own money text, with the display-currency text beside it when both are known. */
export function amountText(value: Amount, display: IndexDisplay = {}): string {
  const original = money(value.amount, value.currency);
  if (!display.currency) return original;
  const shown = fxDisplay(value, display.currency, display.rates ?? {});
  return shown.converted ? `${original} · ${shown.converted}` : original;
}

/** A message's text on one line, cut to 120 characters, as its search result title. */
const oneLine = (s: string) => s.replace(/\s+/g, " ").trim().slice(0, 120);

/** Builds the index. A task whose project is missing is left out, because its link would not open. */
export function buildRecordIndex(src: IndexSource, display: IndexDisplay = {}): IndexedRecord[] {
  const projectById = new Map(src.projects.map((p) => [p.id, p]));
  const out: IndexedRecord[] = [];
  const add = (ref: RecordRef, extra: string[] = []) => out.push({ ...ref, text: foldText([ref.title, ref.meta ?? "", ...extra].join(" ")) });
  for (const c of src.clients) add({ kind: "client", id: c.id, title: c.name, meta: c.code, href: `/clients/${c.slug}/overview` });
  for (const p of src.projects) add({ kind: "project", id: p.id, title: p.name, meta: p.key, href: `/projects/${p.slug}/list` });
  for (const t of src.tasks) {
    const project = projectById.get(t.projectId);
    if (project) add({ kind: "task", id: t.id, title: t.title, meta: t.key, href: `/projects/${project.slug}/list?peek=${t.key}` });
  }
  for (const i of src.invoices) add({ kind: "invoice", id: i.id, title: i.number, meta: amountText(i.total, display), href: `/invoices/${i.id}` }, [i.total.currency]);
  for (const p of src.people) add({ kind: "person", id: p.id, title: p.name, meta: p.email ?? undefined, href: `/team/${p.id}` }, [p.email ?? ""]);
  for (const c of src.contacts) add({ kind: "contact", id: c.id, title: c.name, meta: c.email ?? undefined, href: "/contacts" }, [c.email ?? ""]);
  for (const d of src.deals) add({ kind: "deal", id: d.id, title: d.title, meta: amountText(d.value, display), href: "/deals" }, [d.value.currency]);
  for (const d of src.docs) add({ kind: "doc", id: d.id, title: d.title, href: `/docs/${d.id}` });
  for (const p of src.proposals ?? []) add({ kind: "proposal", id: p.id, title: p.title, meta: amountText(p.value, display), href: `/proposals/${p.id}/edit` }, [p.value.currency]);
  for (const f of src.files ?? []) add({ kind: "file", id: f.id, title: f.name, href: `/files/${f.folderId}` });
  for (const t of src.tickets ?? []) add({ kind: "ticket", id: t.id, title: t.subject, meta: t.number, href: `/tickets/${t.id}` });
  for (const c of src.conversations ?? []) add({ kind: "conversation", id: c.id, title: c.subject, href: `/inbox/${c.id}` });
  for (const a of src.agreements ?? []) add({ kind: "agreement", id: a.id, title: a.title, href: `/agreements/${a.id}` });
  for (const m of src.messages ?? []) add({ kind: "message", id: m.id, title: oneLine(m.body) || m.fromName, meta: m.fromName, href: `/inbox/${m.conversationId}` }, [m.body]);
  return out;
}

/** Records whose text holds every word of the query. An empty query matches nothing. */
export function searchRecords(index: IndexedRecord[], query: string, limit = Number.POSITIVE_INFINITY): IndexedRecord[] {
  const words = foldText(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const hits: IndexedRecord[] = [];
  for (const r of index) {
    if (hits.length >= limit) break;
    if (words.every((w) => r.text.includes(w))) hits.push(r);
  }
  return hits;
}

/** The part of a record that is kept in favourites and recents (no search text). */
export function refOf(r: RecordRef): RecordRef {
  return { kind: r.kind, id: r.id, title: r.title, href: r.href, meta: r.meta };
}

export const sameRecord = (a: RecordRef, b: RecordRef) => a.kind === b.kind && a.id === b.id;

/** Type guard for values read back from browser storage. */
export function isRecordRef(value: unknown): value is RecordRef {
  if (typeof value !== "object" || value === null) return false;
  const r = value as Record<string, unknown>;
  return (
    typeof r.kind === "string" &&
    (RECORD_KINDS as readonly string[]).includes(r.kind) &&
    typeof r.id === "string" &&
    typeof r.title === "string" &&
    typeof r.href === "string" &&
    (r.meta === undefined || typeof r.meta === "string")
  );
}

